import {
	DocumentsViewModel,
	DocumentTemplateModel,
	ListLocationViewModel,
	ListLocationModel,
	DocumentsViewType
} from "client/Api/Documents/SharePoint/common";
import { useEffect, useMemo } from "react";
import { never } from "client_legacy/ndaw/errors";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { getAllDocumentTemplates, getListDocumentTemplates } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { useForceRerender } from "client/Hooks";
import { delay } from "client/Misc/utils";
import { TemplateAvailabilityKind } from "client/Api/SharePointSettings/sharepointSettings";

type DocumentTemplateLocation =
	{
		key: "";
		kind: "global";
	}
	|
	{
		key: string;
		kind: "list";
		list: ListLocationViewModel;
	};

export type DocumentTemplateState =
	{
		isLoading: true;
	}
	|
	{
		isLoading: false;
		templates: readonly DocumentTemplateModel[];
	};

const _documentTemplateCache = new Map<string, readonly DocumentTemplateModel[]>();

export function useDocumentTemplates(
	templateAvailabilityKind: TemplateAvailabilityKind,
	view: DocumentsViewModel
): DocumentTemplateState {
	const location: DocumentTemplateLocation | undefined = useLocation(templateAvailabilityKind, view);

	const templates: readonly DocumentTemplateModel[] | undefined = location
		? _documentTemplateCache.get(location.key)
		: undefined;

	const [forceRerender] = useForceRerender();

	useEffect(
		(): (() => void) | undefined => {
			if (!location) {
				return;
			}

			if (templates) {
				return;
			}

			const loadTemplates = async (
				location: DocumentTemplateLocation,
				signal: AbortSignal
			): Promise<void> => {

				let templates: readonly DocumentTemplateModel[];
				try {
					// Queue it later when the rest of the page has settled a bit
					// A browser has a limited number of concurrent requests,
					// so other more important request will be processed first.
					await delay(500, signal);

					switch (location.kind) {
						case "global":
						{
							templates = await getAllDocumentTemplates();
							break;
						}
						case "list":
						{
							const locationModel: ListLocationModel = convertToLocationModel(location.list);
							templates = await getListDocumentTemplates(locationModel, signal);
							break;
						}
						default:
							never(location);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					templates = [];
					console.error(e);
				}

				_documentTemplateCache.set(location.key, templates);
				forceRerender();
			};

			const cancellation = new AbortController();
			loadTemplates(location, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			location,
			templates,
			forceRerender
		]
	);

	return useMemo(
		(): DocumentTemplateState => {
			if (!location) {
				return {
					isLoading: false,
					templates: []
				};
			}

			return templates
				? { isLoading: false, templates }
				: { isLoading: true };
		},
		[location, templates]
	);
};

function useLocation(
	kind: TemplateAvailabilityKind,
	view: DocumentsViewModel
): DocumentTemplateLocation | undefined {
	return useMemo(
		(): DocumentTemplateLocation | undefined => {
			switch (view.type)
			{
				case DocumentsViewType.RecycleBinView:
				case DocumentsViewType.SharedWithMeView:
				case DocumentsViewType.RecentlyModified:
					return undefined;
				case DocumentsViewType.EditableView:
					switch (kind) {
						case TemplateAvailabilityKind.Company:
							return {
								key: "",
								kind: "global"
							};
						case TemplateAvailabilityKind.Library:
							return {
								key: view.location.siteUrl + ":" + view.location.listId,
								kind: "list",
								list: view.location
							};
						default:
							return never(kind);
					}
				default:
					never(view.type);
			}

		},
		[
			kind,
			view.type,
			view.location
		]
	);
}